import { useCallback, useEffect, useMemo } from 'react';
import { FormattedMessage, useIntl, defineMessages } from 'react-intl';
import { useSelector } from 'react-redux';

import { Button } from 'ui-components/components/button';
import { Checkbox } from 'ui-components/components/checkbox';
import { FloatInput } from 'ui-components/components/float-input';
import { Text, TextType } from 'ui-components/components/text';
import { FlexColumn } from 'ui-components/components/flex/column';
import { FlexRow } from 'ui-components/components/flex/row';
import { Gap } from 'ui-components/components/flex/gap';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';

import ShipFromDropdown from '../../../../../../fulfillment-planner/ship-from/ship-from-dropdown';
import { ShipFromSearchableDropdown } from '../../../../../../fulfillment-planner/ship-from/ship-from-searchable-dropdown';
import { useEnableShipFromSearchableDropdown } from '../../../../../../feature-flags/hooks/use-enable-ship-from-searchable-dropdown';
import Global from '../../../../../../store/global-selectors';
import { useFreightQuote } from '../../freight-quote-context';

import style from './ship-from-step.module.less';

const shipFromStepMessages = defineMessages({
  shipFromLabel: {
    id: 'UnbymV',
    description: 'Ship From location dropdown label',
    defaultMessage: 'Ship From Location',
  },
  shipDateLabel: {
    id: 'AMAav4',
    description: 'Ship date input label in ship-from step',
    defaultMessage: 'Ship Date',
  },
  pickupServicesLabel: {
    id: '8fXOww',
    description: 'Pickup services section label in ship-from step',
    defaultMessage: 'Pickup Services',
  },
  liftgatePickup: {
    id: 'iKZVCA',
    description: 'Liftgate Pickup service checkbox label',
    defaultMessage: 'Liftgate Pickup',
  },
  insidePickup: {
    id: '630cbC',
    description: 'Inside Pickup service checkbox label',
    defaultMessage: 'Inside Pickup',
  },
  residentialPickup: {
    id: 'hF78ks',
    description: 'Residential Pickup service checkbox label',
    defaultMessage: 'Residential Pickup',
  },
  tradeShowPickup: {
    id: 'MbQYR3',
    description: 'Tradeshow Pickup service checkbox label',
    defaultMessage: 'Tradeshow Pickup',
  },
  constructionSitePickup: {
    id: 'k27rPe',
    description: 'Construction Site Pickup service checkbox label',
    defaultMessage: 'Construction Site Pickup',
  },
  notifyBeforePickup: {
    id: 'giNhaz',
    description: 'Notify Me Before Pickup service checkbox label',
    defaultMessage: 'Notify Me Before Pickup',
  },
  dropAtTerminal: {
    id: 'kL8+Oe',
    description: 'Drop Shipment at Terminal service checkbox label',
    defaultMessage: 'Drop Shipment at Terminal',
  },
  saveButton: {
    id: 'pgkf0u',
    description: 'Save button in ship-from step',
    defaultMessage: 'Save',
  },
  collapsedSummaryDate: {
    id: 'bfgvWd',
    description: 'Ship date summary label in collapsed ship-from step',
    defaultMessage: 'Ship Date: {date}',
  },
});

type ShipFromStepProps = {
  onSave: () => void;
};

export const ShipFromStepSummary = () => {
  const { state } = useFreightQuote();
  const shipFroms = useSelector(Global.selectSellerShipFroms);

  const selectedShipFrom = useMemo(() => {
    if (!state.selectedShipFromId || !shipFroms) return null;
    return (shipFroms as Array<{ shipFromId: number; name: string }>).find(
      (sf) => String(sf.shipFromId) === String(state.selectedShipFromId),
    );
  }, [state.selectedShipFromId, shipFroms]);

  const services = state.additionalServices;
  const activeServices = [
    services.isLiftgatePickup && 'Liftgate Pickup',
    services.isInsidePickup && 'Inside Pickup',
    services.isResidentialPickup && 'Residential Pickup',
    services.isTradeShowPickup && 'Tradeshow Pickup',
    services.isConstructionSitePickup && 'Construction Site Pickup',
    services.isNotifyBeforePickup && 'Notify Before Pickup',
  ].filter(Boolean) as string[];

  return (
    <FlexColumn gap={Gap.Space4}>
      {selectedShipFrom && <Text textType={TextType.Caption} value={selectedShipFrom.name} />}
      {state.shipmentDate && <Text textType={TextType.Caption} value={`Ship Date: ${state.shipmentDate}`} />}
      {activeServices.length > 0 && (
        <FlexRow className={style['service-tags']} gap={Gap.Space4}>
          {activeServices.map((svc) => (
            <span className={style['service-tag']} key={svc}>
              {svc}
            </span>
          ))}
        </FlexRow>
      )}
    </FlexColumn>
  );
};

export const ShipFromStep = ({ onSave }: ShipFromStepProps) => {
  const intl = useIntl();
  const { state, localDispatch } = useFreightQuote();
  const shipFroms = useSelector(Global.selectSellerShipFroms);
  const shouldUseSearchableDropdown = useEnableShipFromSearchableDropdown();

  const shipFromOptions = useMemo(
    () =>
      (shipFroms ?? []).map((sf: { shipFromId: number; name: string }) => ({
        id: sf.shipFromId,
        name: sf.name,
      })),
    [shipFroms],
  );

  useEffect(() => {
    if (!state.shipmentDate) {
      const today = new Date().toISOString().split('T')[0];
      localDispatch({ type: 'UPDATE_SHIPMENT_DATE', shipmentDate: today });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleShipFromChange = useCallback(
    (shipFromId: string) => {
      localDispatch({ type: 'SET_SELECTED_SHIP_FROM', shipFromId });
    },
    [localDispatch],
  );

  const handleShipDateChange = useCallback(
    (value: string) => {
      localDispatch({ type: 'UPDATE_SHIPMENT_DATE', shipmentDate: value });
    },
    [localDispatch],
  );

  const handleServiceToggle = useCallback(
    (field: string, checked: boolean) => {
      localDispatch({
        type: 'UPDATE_ADDITIONAL_SERVICES',
        additionalServices: { ...state.additionalServices, [field]: checked },
      });
    },
    [localDispatch, state.additionalServices],
  );

  const services = state.additionalServices;

  return (
    <FlexColumn gap={Gap.Space16}>
      <div>
        <Text textType={TextType.Label} value={<FormattedMessage {...shipFromStepMessages.shipFromLabel} />} />
        <div className={style['ship-from-field']}>
          {shouldUseSearchableDropdown ? (
            <ShipFromSearchableDropdown
              onChange={handleShipFromChange}
              selectedShipFromId={state.selectedShipFromId}
              shipFroms={shipFromOptions}
            />
          ) : (
            <ShipFromDropdown onChange={handleShipFromChange} selectedShipFromId={state.selectedShipFromId} shipFroms={shipFromOptions} />
          )}
        </div>
      </div>

      <FloatInput
        label={intl.formatMessage(shipFromStepMessages.shipDateLabel)}
        onChange={handleShipDateChange}
        type='date'
        value={state.shipmentDate}
      />

      <div>
        <Text isBold textType={TextType.Caption} value={<FormattedMessage {...shipFromStepMessages.pickupServicesLabel} />} />
        <div className={style['services-grid']}>
          <Checkbox
            checked={services.isLiftgatePickup}
            label={intl.formatMessage(shipFromStepMessages.liftgatePickup)}
            onChange={(checked: boolean) => handleServiceToggle('isLiftgatePickup', checked)}
          />
          <Checkbox
            checked={services.isInsidePickup}
            label={intl.formatMessage(shipFromStepMessages.insidePickup)}
            onChange={(checked: boolean) => handleServiceToggle('isInsidePickup', checked)}
          />
          <Checkbox
            checked={services.isResidentialPickup}
            label={intl.formatMessage(shipFromStepMessages.residentialPickup)}
            onChange={(checked: boolean) => handleServiceToggle('isResidentialPickup', checked)}
          />
          <Checkbox
            checked={services.isTradeShowPickup}
            label={intl.formatMessage(shipFromStepMessages.tradeShowPickup)}
            onChange={(checked: boolean) => handleServiceToggle('isTradeShowPickup', checked)}
          />
          <Checkbox
            checked={services.isConstructionSitePickup}
            label={intl.formatMessage(shipFromStepMessages.constructionSitePickup)}
            onChange={(checked: boolean) => handleServiceToggle('isConstructionSitePickup', checked)}
          />
          <Checkbox
            checked={services.isNotifyBeforePickup}
            label={intl.formatMessage(shipFromStepMessages.notifyBeforePickup)}
            onChange={(checked: boolean) => handleServiceToggle('isNotifyBeforePickup', checked)}
          />
        </div>
      </div>

      <div className={style['step-actions']}>
        <Button onClick={onSave} theme={THEME_CONSTANTS.primary}>
          <FormattedMessage {...shipFromStepMessages.saveButton} />
        </Button>
      </div>
    </FlexColumn>
  );
};
